import React, { useCallback, useEffect, useRef } from 'react';
import { easeOutExpo } from 'utils/numbers';

interface Props {
  className?: string;

  /**
   * Animation duration in milliseconds.
   * @default 300
   */
  duration?: number;

  /**
   * @default Number.prototype.toLocaleString
   */
  format?: (v: number) => string;

  /**
   * @default 0
   */
  fractionDigits?: number;

  /**
   * @default 0
   */
  value?: number;
}

const AnimatedNumber: React.FC<Props> = ({
  className,
  fractionDigits = 0,
  duration = 300,
  format: format_,
  value = 0,
}) => {
  const animationRef = useRef<number | null>(null);
  const oldValueRef = useRef<number>(0);
  const elementRef = useRef<HTMLElement>(null);

  duration = Math.max(duration, 0);
  fractionDigits = Math.max(fractionDigits, 0);

  const format = useCallback(
    (v: number) =>
      format_?.(v) ??
      v.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    [format_, fractionDigits]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    if (!duration || duration < 0) {
      oldValueRef.current = value;
      element.innerText = format(value);
      return;
    }

    const oldValue = oldValueRef.current;
    const difference = value - oldValue;

    let start: DOMHighResTimeStamp;
    const animate = (timestamp: DOMHighResTimeStamp) => {
      if (start === undefined) {
        start = timestamp;
      }

      const elapsed = timestamp - start;
      const currentValue = oldValue + difference * easeOutExpo(Math.min(elapsed / duration, 1));

      element.innerText = format(currentValue);

      if (elapsed < duration) {
        animationRef.current = window.requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        oldValueRef.current = value;
      }
    };

    animationRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationRef.current !== null) {
        window.cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [duration, format, fractionDigits, value]);

  return (
    <span ref={elementRef} className={className}>
      {format(oldValueRef.current)}
    </span>
  );
};

export default React.memo(AnimatedNumber);
